/**
 * 튜닝 사례 리파지토리 화면 — 쌓인 사례를 훑어보고, 찾고, 쓰고, 고친다.
 *
 * **등록도 여기서 한다.** 예전에는 대화로만 등록했는데, 흐름이 정해져 있는 일을 채팅으로
 * 몰면 사용자가 매번 말로 지시해야 했다. 이제 등록은 같은 모달 안의 양식(`CaseRegisterForm`)
 * 이고, LLM 은 「첨부에서 양식을 채운다」 한 가지만 한다. 읽는 곳과 쓰는 곳이 붙어 있어야
 * 방금 쓴 것이 목록에 어떻게 들어갔는지 바로 보인다.
 *
 * 사례가 검색에 걸리려면 `final` 이라야 하므로 상태를 눈에 띄게 보여 준다 — 초안인 줄 모르고
 * "왜 튜닝할 때 참고가 안 되지" 하는 일을 막는다.
 *
 * 한글 검색은 3글자 이상이라야 걸린다(FTS5 trigram 제약). 서버가 그 사실을 note 로 주므로
 * 그대로 띄운다 — 조용히 빈 결과를 보여 주면 사용자가 원인을 모른다.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { formatApiError } from '../../utils/formatters';
// 확인은 공통 대화상자로 — 브라우저 기본 confirm 은 앱과 모양이 달라 다른 프로그램이 묻는 것처럼 보인다
import { useConfirm } from '../ConfirmDialog';
import { FiX, FiSearch, FiTrash2, FiBookOpen, FiAlertCircle, FiPlus, FiEdit3, FiTag, FiFolder, FiFolderPlus, FiCopy, FiClock, FiRotateCcw } from 'react-icons/fi';
import CaseVersions from './CaseVersions';
import { listTuningCases, getTuningCase, deleteTuningCase, getCaseTopics, getCaseFolders, createCaseFolder, renameCaseFolder, deleteCaseFolder } from '../../services/assistantClient';
// 이름 입력은 공통 대화상자로(브라우저 prompt 금지)
import { usePrompt } from '../PromptDialog';
import TopicManager from './TopicManager';
import GuideManager from './GuideManager';
import { logAction } from '../../services/opsLog';
import CaseRegisterForm from './CaseRegisterForm';
import useModalWindow from '../common/useModalWindow';
import './TuningCaseModal.css';
// 사례 상세는 SQL 튜닝 리포트와 같은 보기(문서/원문 · 감소율 막대 · 한글 2칸 고정폭)로 — 2026-10-11
import ReportView from '../sqlTuning/ReportView';
import { caseDocument } from './caseDocument';
import { USAGES } from '../../utils/caseMeta';
import { storedTime } from '../../utils/storedTime';
import memoWindow from '../common/memoWindow';

const VIS = ['', 'generic', 'internal'];
const STATUS = ['', 'final', 'draft'];
/** 분류 줄의 축 순서 — 뼈대(증상 → 원인 → 처방) 다음 선택 축 영역. 서버 taxonomy 의 축 id 와 같다 */
const TOPIC_AXES = ['symptom', 'cause', 'fix', 'area'];

/**
 * 사례 제목 「원인 — 개선 방안」 의 뒷부분(개선 방안)을 작고 옅게 — 앞부분이 먼저 읽히게(사용자 요청 2026-10-11).
 * 보여 줄 때만 가른다(저장된 제목은 그대로). 구분자(—)가 없으면 통째로 한 덩어리.
 */
function CaseTitle({ title }) {
    const text = String(title || '');
    const at = text.indexOf(' — ');
    if (at < 0) return text;
    return (
        <>
            {text.slice(0, at)}
            <span className="tcase-titleSub">{text.slice(at)}</span>
        </>
    );
}
CaseTitle.propTypes = { title: PropTypes.string };

/**
 * 사례 상세 머리줄의 속성 — DBMS · 공개 범위 · 상태 · 용도 · 근거 · 폴더(2026-10-24 사용자 요청: 보기 단추 자리에).
 * 목록의 분류 줄과 같은 칩 모양이다. 용도를 정하지 않아 내용에서 정했으면 「자동」 을 붙인다.
 */
function CaseMetaChips({ c, t }) {
    if (!c) return null;
    return (
        <>
            {c.dbms ? <span className="tcase-tag">{c.dbms}{c.dbmsVersion ? ` ${c.dbmsVersion}` : ''}</span> : null}
            <span className={`tcase-tag tcase-vis-${c.visibility}`}>{t(`assistant:caseList.vis.${c.visibility}`)}</span>
            {c.visibility === 'generic' && c.redactionMode === 'none' ? (
                <span className="tcase-tag tcase-st-draft">{t('assistant:caseList.noRedaction')}</span>
            ) : null}
            <span className={`tcase-tag tcase-st-${c.status}`}>{t(`assistant:caseList.status.${c.status}`)}</span>
            {(c.usage || []).map(u => (
                <span className="tcase-tag tcase-usage" key={u} title={c.usageDerived ? t('assistant:caseMeta.auto') : undefined}>
                    {t(`assistant:caseMeta.u.${u}`)}{c.usageDerived ? ` (${t('assistant:caseMeta.auto')})` : ''}
                </span>
            ))}
            {c.perfBasis ? <span className="tcase-tag" title={t('assistant:caseMeta.perfBasis')}>{t(`assistant:caseMeta.basis.${c.perfBasis}`)}</span> : null}
            {c.folder ? <span className="tcase-tag tcase-folderTag"><FiFolder size={9} /> {c.folder}</span> : null}
        </>
    );
}
CaseMetaChips.propTypes = { c: PropTypes.object, t: PropTypes.func.isRequired };

function TuningCaseModal({ open, seed, onSeedUsed, onClose, provider, onCaseSaved, onOpenGuides, restore, onView, onSelectedChange }) {
    const { t } = useTranslation();
    const confirm = useConfirm();
    const prompt = usePrompt();
    const [q, setQ] = useState('');
    /** 폴더 — '' = 전체. 고른 폴더는 하위 폴더까지 보이고, 「새 사례」 의 기본 폴더가 된다(2026-10-11) */
    const [folder, setFolder] = useState('');
    const [folders, setFolders] = useState([]);
    const [visibility, setVisibility] = useState('');
    const [status, setStatus] = useState('');
    // 용도로 거르기(AI 참조·교육·챌린지 후보) — 정하지 않은 사례는 서버가 내용에서 정한 값으로 거른다
    const [usage, setUsage] = useState('');
    const [cases, setCases] = useState([]);
    const [note, setNote] = useState('');
    const [selected, setSelected] = useState(null);
    // 지난 판 보기(2026-10-24) — 고른 사례가 바뀌면 지금 판으로 돌아간다
    const [versionView, setVersionView] = useState(null);
    const selectedId = selected ? selected.id : null;
    useEffect(() => { setVersionView(null); }, [selectedId]);
    // 지금 고른 사례를 App 에 알린다 — 에이전트의 「지금 앱 화면」 에 「열린 튜닝 사례」 로 실린다(그림 넣기 등이 어느 사례인지)
    useEffect(() => { if (onSelectedChange) onSelectedChange(open ? selected : null); }, [open, selected, onSelectedChange]);
    // 상세 보기 범위 — 'full' 전체 / 'brief' 축약(개선 효과·분류·내용·유의사항만). SQL 튜닝 리포트의 전체/축약과 같은 뜻
    const [caseView, setCaseView] = useState('full');
    const [loading, setLoading] = useState(false);
    /** null = 목록 / { case: null } = 새 사례 / { case } = 편집. 객체로 두면 "새 사례" 와 "닫힘" 이 갈린다. */
    const [editing, setEditing] = useState(null);
    // 열려 있는 등록·편집 화면에 건넬 에이전트의 양식(덮지 않는다 — 위 seed 처리)
    const [agentDraft, setAgentDraft] = useState(null);
    const editingRef = useRef(editing);
    editingRef.current = editing;
    /** 분류로 거르기. **쌓인 것이 보여야 고른다** — 개수를 함께 그린다. */
    const [topicCatalog, setTopicCatalog] = useState([]);
    const [topicCounts, setTopicCounts] = useState({});
    const [pickedTopics, setPickedTopics] = useState([]);
    /** 유형 관리 화면(증상·원인·처방 유형 추가·수정). 목록·등록과 배타적으로 연다. */
    // 관리 화면이 둘이다: 분류 유형(topics) 과 튜닝 가이드(guides).
    // **다른 물건이다** — 분류는 사례를 찾는 열쇠이고, 가이드는 매 턴 프롬프트에 실리는 규칙이다.
    const [managing, setManaging] = useState(null);   // null | 'topics' | 'guides'
    /** 코드 → 라벨. **라벨은 서버가 준다** — 사용자가 유형을 늘리므로 화면에 적어 둘 수 없다. */
    const labelOf = useCallback(
        (code) => (topicCatalog.find(tp => tp.code === code) || {}).label || code,
        [topicCatalog],
    );
    const axisOfTopic = useCallback((code) => (topicCatalog.find(tp => tp.code === code) || {}).axis, [topicCatalog]);
    const supportTopics = useMemo(() => new Set(topicCatalog.filter(tp => tp.support).map(tp => tp.code)), [topicCatalog]);
    // 분류 줄은 **축을 먼저 고른다**(2026-10-24) — 고른 축은 이 PC 에 기억한다
    const [savedAxis, setTopicAxisState] = useState(() => {
        try { return localStorage.getItem('livesql_tcase_axis') || 'symptom'; } catch (_e) { return 'symptom'; }
    });
    const setTopicAxis = useCallback((ax) => {
        setTopicAxisState(ax);
        try { localStorage.setItem('livesql_tcase_axis', ax); } catch (_e) { /* 기억 못 해도 동작한다 */ }
    }, []);
    // 축마다 사례가 있는 분류 수 · 그 축에서 고른 수 — 사례가 하나도 없는 축은 단추를 세우지 않는다
    const topicAxes = useMemo(() => TOPIC_AXES.map(axis => ({
        axis,
        n: topicCatalog.filter(tp => tp.axis === axis && topicCounts[tp.code]).length,
        picked: topicCatalog.filter(tp => tp.axis === axis && pickedTopics.includes(tp.code)).length,
    })).filter(ax => ax.n > 0 || ax.picked > 0), [topicCatalog, topicCounts, pickedTopics]);
    // 기억한 축에 지금 조건(폴더 등)의 분류가 없으면 있는 첫 축을 보인다(빈 줄이 되지 않게)
    const topicAxis = topicAxes.some(ax => ax.axis === savedAxis) ? savedAxis : ((topicAxes[0] && topicAxes[0].axis) || savedAxis);
    // 고른 축의 분류(사례 많은 순) + 다른 축에서 고른 분류(풀 수 있게 끝에)
    const shownTopics = useMemo(() => {
        const inAxis = topicCatalog
            .filter(tp => tp.axis === topicAxis && (topicCounts[tp.code] || pickedTopics.includes(tp.code)))
            .sort((a, b) => (topicCounts[b.code] || 0) - (topicCounts[a.code] || 0));
        const others = topicCatalog.filter(tp => tp.axis !== topicAxis && pickedTopics.includes(tp.code));
        return [...inAxis, ...others];
    }, [topicCatalog, topicCounts, pickedTopics, topicAxis]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await listTuningCases({ q: q.trim(), visibility, status, includeDrafts: true, topics: pickedTopics, folder, usage });
            setCases(r.cases || []);
            setNote(r.note || '');
            // 목록이 바뀌면 선택이 사라질 수 있다 — 남아 있으면 유지한다
            setSelected(prev => (prev && (r.cases || []).some(c => c.id === prev.id) ? prev : null));
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, [q, visibility, status, pickedTopics, folder, usage]);

    useEffect(() => { if (open) load(); }, [open, load]);

    const loadFolders = useCallback(() => {
        Promise.resolve().then(() => getCaseFolders())
            .then(r => {
                const list = (r && r.folders) || [];
                setFolders(list);
                // 고른 폴더가 사라졌으면(이름 바꿈·지움) 전체로
                setFolder(prev => (prev && !list.some(f => f.path === prev) ? '' : prev));
            })
            .catch(() => { /* 폴더를 못 읽어도 목록은 보여야 한다 */ });
    }, []);
    useEffect(() => { if (open) loadFolders(); }, [open, cases.length, loadFolders]);

    // 새 폴더 — 지금 고른 폴더가 있으면 그 아래에 만든다
    const addFolder = useCallback(async () => {
        const name = await prompt({
            title: t('assistant:caseFolder.newTitle'),
            message: folder ? t('assistant:caseFolder.newUnder', { parent: folder }) : t('assistant:caseFolder.newHint'),
            placeholder: t('assistant:caseFolder.namePlaceholder'),
            confirmText: t('assistant:caseFolder.create'),
        });
        if (!name || !String(name).trim()) return;
        try {
            const r = await createCaseFolder(folder ? `${folder}/${String(name).trim()}` : String(name).trim());
            loadFolders();
            setFolder(r.path);
            toast.success(t('assistant:caseFolder.created', { path: r.path }));
        } catch (err) { toast.error(err.message); }
    }, [prompt, folder, loadFolders, t]);

    const renameFolder = useCallback(async () => {
        if (!folder) return;
        const name = await prompt({
            title: t('assistant:caseFolder.renameTitle'),
            message: t('assistant:caseFolder.renameHint'),
            defaultValue: folder,
            confirmText: t('assistant:caseFolder.rename'),
        });
        if (!name || String(name).trim() === folder) return;
        try {
            const r = await renameCaseFolder(folder, String(name).trim());
            setFolder(r.path);
            loadFolders();
            load();
        } catch (err) { toast.error(err.message); }
    }, [prompt, folder, loadFolders, load, t]);

    const removeFolder = useCallback(async () => {
        if (!folder) return;
        const ok = await confirm({
            title: t('assistant:caseFolder.deleteTitle'),
            message: t('assistant:caseFolder.deleteConfirm', { path: folder }),
            confirmText: t('common:ui.delete'),
            variant: 'danger',
        });
        if (!ok) return;
        try {
            await deleteCaseFolder(folder);
            setFolder('');
            loadFolders();
        } catch (err) { toast.error(err.message); }
    }, [confirm, folder, loadFolders, t]);

    /**
     * 에이전트가 채워 보낸 초안 — **바로 편집 상태로 연다.**
     *
     * 대화에서 "이걸 사례로 등록해줘" 라고 하면 모델이 양식을 채워 보내고, 사용자는 목록을
     * 거치지 않고 그 자리에서 고쳐 저장한다. `nonce` 로 같은 내용을 다시 보내도 알아챈다.
     */
    useEffect(() => {
        if (!open || !seed) return;
        // 에이전트가 대화의 이미지를 **기존 사례의 그림**으로 넣었다(add_case_figures, 2026-10-24) — 그 사례의 편집 화면을 그림 탭에서 연다.
        // 그림은 대기 상태라 사용자가 저장해야 붙는다. 이미 편집 중이면 덮지 않는다(고치던 것이 사라진다)
        if (seed.addFigures) {
            const { caseId, figures: add } = seed.addFigures;
            if (onSeedUsed) onSeedUsed();
            if (editingRef.current) { toast.warning(t('assistant:caseFig.agentBusy')); return; }
            getTuningCase(caseId).then((c) => {
                if (!c) return;
                setSelected(c);
                const extra = (add || []).map(f => ({ id: f.id, caption: f.caption || '', shared: false, filename: f.filename || '', width: 100, bg: 'light' }));
                setEditing({ case: { ...c, figures: [...(c.figures || []), ...extra] }, tab: 'figures' });
                toast.info(t('assistant:caseFig.agentAdded', { n: extra.length }));
            }).catch((err) => toast.error(formatApiError(err, t)));
            return;
        }
        // 에이전트가 **기존 사례**를 고친 양식(show_case_draft 의 caseId, 2026-10-24) — 새 사례가 아니라 그 사례의 편집으로 연다.
        // 예전에는 id 없이 와서 새 사례로 저장돼 같은 사례가 둘이 되고, 붙여 둔 그림이 없는 쪽이 목록 맨 앞에 섰다(신고).
        // 보낸 칸만 덮고 나머지(그림·폴더·원본 파일 등)는 그 사례의 것을 둔다. 편집 중이면 그 사례일 때만 「에이전트가 고친 양식」 으로 건넨다
        if (seed.caseId) {
            const { caseId, nonce, ...fields } = seed;
            if (onSeedUsed) onSeedUsed();
            const cur = editingRef.current;
            if (cur) {
                if (cur.case && cur.case.id === caseId) setAgentDraft(seed);
                else toast.warning(t('assistant:caseFig.agentBusy'));
                return;
            }
            getTuningCase(caseId).then((c) => {
                if (!c) return;
                setSelected(c);
                const merged = { ...c, ...fields, id: c.id };
                if (!Array.isArray(fields.topics) || !fields.topics.length) merged.topics = c.topics;
                if (!fields.redaction) merged.redaction = c.redaction;
                setEditing({ case: merged });
            }).catch((err) => toast.error(formatApiError(err, t)));
            return;
        }
        // 첨부에서 등록(open_case_register) — 양식은 비우고 첨부만 실어 연다. 화면의 절차(공개 범위 → AI 초안)가 이어진다
        if (seed.register) setEditing({ case: null, preload: { sources: seed.sources || [], instruction: seed.instruction || '', nonce: seed.nonce } });
        // 등록·편집 화면이 **이미 열려 있으면 덮지 않는다** — 사용자가 고치던 것이 사라진다. 그 화면에 「에이전트가 고친 양식」 으로 건네고
        // 반영할지는 사용자가 고른다(화면의 요청 칸으로 보완을 맡겼을 때의 답이 이 길로 온다)
        else if (editingRef.current) setAgentDraft(seed);
        else setEditing({ case: seed });
        if (onSeedUsed) onSeedUsed();
    }, [open, seed, onSeedUsed]);

    // 분류 옆의 수는 **지금 목록의 조건**(폴더·공개 범위·상태·용도)으로 센다 — 폴더를 골라도 전체 수가 보였다(2026-10-23 신고)
    const loadTopics = useCallback(() => {
        getCaseTopics(false, { folder, visibility, status, usage })
            .then(r => { setTopicCatalog(r.topics || []); setTopicCounts(r.counts || {}); })
            .catch(() => { /* 분류를 못 읽어도 목록은 보여야 한다 */ });
    }, [folder, visibility, status, usage]);
    useEffect(() => { if (open) loadTopics(); }, [open, cases.length, loadTopics]);

    const openCase = useCallback(async (id) => {
        try { setSelected(await getTuningCase(id)); }
        catch (err) { toast.error(err.message); }
    }, []);

    // 새로고침 뒤 — 보던 사례를 한 번만 다시 연다(App 이 workspaceLayout 에서 준 restore). 에이전트 초안(seed)이 있으면 그쪽이 앞선다.
    // 지워졌거나 못 보는 사례면 조용히 목록만. 되돌리기가 끝난 뒤에야 위치를 알린다 — 먼저 알리면 「선택 없음」 이 저장된 위치를 덮는다.
    const [viewReady, setViewReady] = useState(false);
    useEffect(() => {
        if (!open || viewReady) return undefined;
        if (!(restore && restore.id) || seed) { setViewReady(true); return undefined; }
        let alive = true;
        getTuningCase(restore.id)
            .then((c) => { if (alive) setSelected(c); })
            .catch(() => { /* 없는 사례 — 목록만 */ })
            .finally(() => { if (alive) setViewReady(true); });
        return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 처음 열릴 때 한 번
    }, [open]);
    const viewCaseId = selected ? selected.id : null;
    useEffect(() => { if (open && viewReady && onView) onView({ id: viewCaseId, step: null }); }, [open, viewReady, viewCaseId, onView]);

    const remove = useCallback(async (id, title) => {
        const ok = await confirm({
            title: t('assistant:caseList.deleteTitle'),
            message: t('assistant:caseList.confirmDelete', { title }),
            confirmText: t('common:ui.delete'),
            variant: 'danger',
        });
        if (!ok) return;
        try {
            await deleteTuningCase(id);
            toast.success(t('assistant:caseList.deleted'));
            if (selected && selected.id === id) setSelected(null);
            load();
        } catch (err) { toast.error(err.message); }
    }, [selected, load, t, confirm]);

    const summary = useMemo(() => {
        const n = { final: 0, draft: 0, generic: 0, internal: 0 };
        for (const c of cases) { n[c.status] = (n[c.status] || 0) + 1; n[c.visibility] = (n[c.visibility] || 0) + 1; }
        return n;
    }, [cases]);

    /** 저장 직후 그 사례를 펼쳐 보여 준다 — 방금 쓴 것이 목록에 어떻게 들어갔는지 바로 보인다. */
    /**
     * @param opts.keepOpen 자료에 사례가 더 남아 있을 때. 양식을 닫으면 첨부와 「찾은 사례
     *   목록」이 함께 사라져, 나머지를 등록하려면 같은 자료를 처음부터 다시 올려야 한다.
     */
    const afterSave = useCallback((saved, opts) => {
        // 어디서 온 양식이었는지(예: SQL 튜닝 작업의 fromSqlTuneJob)를 함께 넘긴다 — 부른 쪽이 그 작업에 잇는다
        if (onCaseSaved && saved) onCaseSaved(saved, editing && editing.case);
        load();
        if (opts && opts.keepOpen) return;
        setEditing(null);
        if (saved && saved.id) openCase(saved.id);
    }, [load, openCase, onCaseSaved, editing]);

    /**
     * 작성 중에는 **바깥 클릭으로 닫지 않는다.** 사례 하나가 첨부·분석·수정을 거친 결과라
     * 실수로 닫으면 그게 통째로 날아간다. 닫기 버튼은 물어보고 닫는다.
     */
    const confirmDiscard = useCallback(() => confirm({
        title: t('assistant:caseForm.discardTitle'),
        message: t('assistant:caseForm.confirmDiscard'),
        confirmText: t('assistant:caseForm.discardConfirm'),
        variant: 'danger',
    }), [confirm, t]);
    // 닫기(X) — 등록·편집 중이면 창을 닫지 않고 **튜닝 사례 화면(목록·상세)으로 돌아간다**(사용자 요청 2026-10-11). 그다음 X 가 창을 닫는다
    const requestClose = useCallback(async () => {
        if (editing) {
            if (await confirmDiscard()) setEditing(null);
            return;
        }
        onClose();
    }, [editing, onClose, confirmDiscard]);

    // 창처럼 — 끌어 이동 · 최대화 · 새 창으로 분리(다른 모니터에서 전체 화면). common/useModalWindow(사용자 요청 2026-10-11)
    const win = useModalWindow({ title: t('assistant:caseList.title') });
    const { reset: resetWin } = win;
    useEffect(() => { if (!open) resetWin(); }, [open, resetWin]);

    if (!open) return null;

    // 바깥을 눌러 닫기는 앱 안에 떠 있을 때만 — 최대화·분리 중에는 「바깥」 이 없다
    return win.wrap(
        <div className="tcase-overlay" style={win.overlayStyle} onClick={() => { if (!editing && !win.popped && !win.maximized && !win.docked) onClose(); }} role="presentation">
            <div className="app-modal tcase-modal" data-app-modal onClick={(e) => e.stopPropagation()} role="presentation" ref={win.frameRef} style={win.frameStyle}>
                <div className="app-modal-header tcase-header" {...win.headerProps}>
                    <span className="app-modal-title tcase-title">
                        <FiBookOpen size={14} />
                        {managing === 'topics' ? t('assistant:topicManager.title')
                            : managing === 'guides' ? t('assistant:guideManager.title')
                                : (editing ? t('assistant:caseForm.title') : t('assistant:caseList.title'))}
                    </span>
                    <span className="tcase-headerRight">
                        {!editing && !managing ? (
                            <>
                                <button
                                    type="button" className="tcase-new"
                                    // 튜닝 가이드는 문서형이 됐다(2026-10-11) — 설계 가이드와 같은 창의 「튜닝 가이드」 탭을 연다.
                                    // 그 창을 열 수 없는 자리(prop 없음)에서는 예전의 짧은 규칙 관리가 뜬다
                                    onClick={() => { logAction('modal.guideManager'); if (onOpenGuides) onOpenGuides(); else setManaging('guides'); }}
                                >
                                    <FiBookOpen size={12} /> {t('assistant:guideManager.open')}
                                </button>
                                <button
                                    type="button" className="tcase-new"
                                    onClick={() => { logAction('modal.topicManager'); setManaging('topics'); }}
                                >
                                    <FiTag size={12} /> {t('assistant:topicManager.open')}
                                </button>
                                <button type="button" className="tcase-new" onClick={() => setEditing({ case: null, folder })}>
                                    <FiPlus size={12} /> {t('assistant:caseForm.new')}
                                </button>
                            </>
                        ) : null}
                        {managing ? (
                            <button type="button" className="tcase-new" onClick={() => { setManaging(null); loadTopics(); }}>
                                {t('common:ui.close')}
                            </button>
                        ) : null}
                        {win.controls}
                        <button type="button" className="app-modal-close tcase-close" onClick={requestClose} title={t('common:ui.close')}>
                            <FiX size={16} />
                        </button>
                    </span>
                </div>

                {managing ? (
                    <div className="tcase-formWrap">
                        {managing === 'guides' ? <GuideManager /> : <TopicManager />}
                    </div>
                ) : editing ? (
                    <div className="tcase-formWrap">
                        <CaseRegisterForm
                            // 편집 대상이 바뀌면 **새로 만든다** — 남은 입력이 다음 사례로 흘러가면 안 된다.
                            // 에이전트가 보낸 초안은 id 가 없으므로 nonce 로 가른다(다시 보내면 새로 그린다).
                            key={editing.case ? (editing.case.id || editing.case.nonce || 'new') : (editing.preload ? `pre_${editing.preload.nonce}` : 'new')}
                            preload={editing.preload}
                            agentDraft={agentDraft}
                            initial={editing.case}
                            initialTab={editing.tab}
                            // 목록에서 고른 폴더를 새 사례의 기본 폴더로(편집이면 그 사례의 폴더)
                            defaultFolder={editing.case ? undefined : editing.folder}
                            onFoldersChanged={loadFolders}
                            provider={provider}
                            onSaved={afterSave}
                            onCancel={async () => {
                                if (await confirmDiscard()) setEditing(null);
                            }}
                            onManageTopics={() => setManaging('topics')}
                        />
                    </div>
                ) : (
                <>
                <div className="tcase-toolbar">
                    <div className="tcase-search">
                        <FiSearch size={12} />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
                            placeholder={t('assistant:caseList.searchPlaceholder')}
                        />
                    </div>
                    {/* 폴더 — 주인만 보는 정리 경로. 고르면 하위 폴더까지 보인다 */}
                    <span className="tcase-folder">
                        <FiFolder size={12} />
                        <select value={folder} onChange={(e) => setFolder(e.target.value)} title={t('assistant:caseFolder.pick')} aria-label={t('assistant:caseFolder.pick')}>
                            <option value="">{t('assistant:caseFolder.all')}</option>
                            {folders.map(f => (
                                <option key={f.path} value={f.path}>
                                    {`${'\u00a0\u00a0'.repeat(f.path.split('/').length - 1)}${f.path.split('/').pop()} (${f.total})`}
                                </option>
                            ))}
                        </select>
                        <button type="button" className="tcase-iconBtn" onClick={addFolder} title={t('assistant:caseFolder.newTitle')} aria-label={t('assistant:caseFolder.newTitle')}>
                            <FiFolderPlus size={13} />
                        </button>
                        {folder ? (
                            <>
                                <button type="button" className="tcase-iconBtn" onClick={renameFolder} title={t('assistant:caseFolder.renameTitle')} aria-label={t('assistant:caseFolder.renameTitle')}>
                                    <FiEdit3 size={12} />
                                </button>
                                <button type="button" className="tcase-iconBtn" onClick={removeFolder} title={t('assistant:caseFolder.deleteTitle')} aria-label={t('assistant:caseFolder.deleteTitle')}>
                                    <FiTrash2 size={12} />
                                </button>
                            </>
                        ) : null}
                    </span>
                    <select value={visibility} onChange={(e) => setVisibility(e.target.value)}>
                        {VIS.map(v => <option key={v || 'all'} value={v}>{v ? t(`assistant:caseList.vis.${v}`) : t('assistant:caseList.allVis')}</option>)}
                    </select>
                    <select value={status} onChange={(e) => setStatus(e.target.value)}>
                        {STATUS.map(v => <option key={v || 'all'} value={v}>{v ? t(`assistant:caseList.status.${v}`) : t('assistant:caseList.allStatus')}</option>)}
                    </select>
                    <select value={usage} onChange={(e) => setUsage(e.target.value)} aria-label={t('assistant:caseMeta.usage')}>
                        <option value="">{t('assistant:caseMeta.usageAll')}</option>
                        {USAGES.map(u => <option key={u} value={u}>{t(`assistant:caseMeta.u.${u}`)}</option>)}
                    </select>
                    <span className="tcase-count">
                        {t('assistant:caseList.count', { count: cases.length })}
                        {summary.draft ? ` · ${t('assistant:caseList.draftCount', { count: summary.draft })}` : ''}
                    </span>
                </div>

                {/* **쌓인 분류만** 보여 준다. 0건 코드를 늘어놓으면 고를 것을 찾기가 더 어렵다. */}
                {topicCatalog.some(tp => topicCounts[tp.code] || pickedTopics.includes(tp.code)) && (
                    <div className="tcase-topicBar">
                        {/* 「분류 전체」 단추는 뺐다(2026-10-24 사용자 요청 — 축 단추가 맨 앞에 서면서 할 일이 없어 보였다). 고른 분류를 한꺼번에
                            푸는 일은 줄 **끝**의 「선택 해제」 가 한다 — 끝에 두면 나타났다 사라져도 다른 단추가 밀리지 않는다 */}
                        {/* 축 먼저 고르기(2026-10-24 사용자 요청) — 증상·원인·처방·영역이 한 줄에 섞여 50개 가까이 늘어서 고를 수가 없었다.
                            고른 축의 분류만 아래에 늘어놓는다. 축 옆 수는 그 축에 사례가 있는 분류의 개수 */}
                        <span className="tcf-axes" role="tablist" aria-label={t('assistant:caseList.axisPick')}>
                            {topicAxes.map(ax => (
                                <button type="button" key={ax.axis} role="tab" aria-selected={topicAxis === ax.axis}
                                    className={`tcf-axis${topicAxis === ax.axis ? ' tcf-axisOn' : ''}`}
                                    onClick={() => setTopicAxis(ax.axis)}>
                                    {t(`assistant:topicAxis.${ax.axis}`)} <em>{ax.n}</em>
                                    {ax.picked > 0 && <i className="tcf-axisDot" aria-label={t('assistant:caseList.axisPicked', { n: ax.picked })} />}
                                </button>
                            ))}
                        </span>
                        {/* 고른 분류는 이 조건에서 0건이어도 남긴다 — 사라지면 풀 수가 없다. 다른 축에서 고른 분류도 여기 남겨 풀 수 있게 한다 */}
                        {shownTopics.map(tp => (
                            <button
                                type="button" key={tp.code}
                                className={`tcf-topic${pickedTopics.includes(tp.code) ? ' tcf-topicOn' : ''}${tp.axis !== topicAxis ? ' tcf-topicOther' : ''}`}
                                title={tp.axis !== topicAxis ? `${t(`assistant:topicAxis.${tp.axis}`)} · ${tp.desc || ''}` : tp.desc}
                                onClick={() => setPickedTopics(prev => (prev.includes(tp.code)
                                    ? prev.filter(x => x !== tp.code)
                                    : [...prev, tp.code]))}
                            >
                                {tp.label} {topicCounts[tp.code] || 0}
                            </button>
                        ))}
                        {pickedTopics.length > 0 && (
                            <button type="button" className="tcf-topic tcf-topicClear" title={t('assistant:caseList.allTopicsHint')}
                                onClick={() => setPickedTopics([])}>
                                <FiX size={10} /> {t('assistant:caseList.clearTopics', { n: pickedTopics.length })}
                            </button>
                        )}
                    </div>
                )}

                {note ? <div className="tcase-note"><FiAlertCircle size={11} /> {note}</div> : null}

                <div className="tcase-body">
                    <ul className="tcase-list">
                        {loading && cases.length === 0 ? <li className="tcase-empty">{t('common:state.loading')}</li> : null}
                        {!loading && cases.length === 0 ? <li className="tcase-empty">{t('assistant:caseList.empty')}</li> : null}
                        {cases.map(c => (
                            <li
                                key={c.id}
                                className={`tcase-item ${selected && selected.id === c.id ? 'tcase-item-on' : ''}`}
                                onClick={() => openCase(c.id)}
                                role="presentation"
                            >
                                <div className="tcase-itemTop">
                                    <span className="tcase-itemTitle"><CaseTitle title={c.title} /></span>
                                    <button
                                        type="button" className="tcase-del"
                                        onClick={(e) => { e.stopPropagation(); remove(c.id, c.title); }}
                                        title={t('common:ui.delete')}
                                    >
                                        <FiTrash2 size={11} />
                                    </button>
                                </div>
                                <div className="tcase-tags">
                                    {c.folder && c.folder !== folder ? (
                                        <span className="tcase-tag tcase-folderTag"><FiFolder size={9} /> {c.folder}</span>
                                    ) : null}
                                    <span className={`tcase-tag tcase-vis-${c.visibility}`}>{t(`assistant:caseList.vis.${c.visibility}`)}</span>
                                    {/* 변조 검증을 건너뛴 공개 사례는 그 사실이 보여야 한다 — 얼마나 믿을지의 근거다 */}
                                    {c.visibility === 'generic' && c.redactionMode === 'none' ? (
                                        <span className="tcase-tag tcase-st-draft">{t('assistant:caseList.noRedaction')}</span>
                                    ) : null}
                                    <span className={`tcase-tag tcase-st-${c.status}`}>{t(`assistant:caseList.status.${c.status}`)}</span>
                                    {c.dbms ? <span className="tcase-tag">{c.dbms}</span> : null}
                                    {/* 용도 — AI 참조뿐이면(대부분) 적지 않는다. 교육·챌린지 후보만 보인다 */}
                                    {(c.usage || []).filter(u => u !== 'ref').map(u => (
                                        <span className="tcase-tag tcase-usage" key={u}>{t(`assistant:caseMeta.u.${u}`)}</span>
                                    ))}
                                    {/* 분류가 빈 축 — 저장은 되지만 그 갈래로는 찾히지 않는다는 것이 보여야 한다 */}
                                    {(c.missingAxes || []).length ? (
                                        <span className="tcase-tag tcase-st-draft" title={t('assistant:caseMeta.incompleteHint', { axes: c.missingAxes.map(a => t(`assistant:topicAxis.${a}`)).join(', ') })}>
                                            {t('assistant:caseMeta.incomplete')}
                                        </span>
                                    ) : null}
                                    {/* 분류 — 영역(맥락)은 목록에서 뺀다(상세에 있다). 보조 처방(힌트)은 흐리게 */}
                                    {(c.topics || []).filter(code => axisOfTopic(code) !== 'area').map(code => (
                                        <span className={`tcase-tag${supportTopics.has(code) ? ' tcase-tagSupport' : ''}`} key={code}>{labelOf(code)}</span>
                                    ))}
                                </div>
                                {/* 증상·개선 효과 설명은 목록에서 뺐다 — 글이 길어 목록이 복잡했다(사용자 요청 2026-10-11). 상세에 다 있다.
                                    줄의 툴팁(증상·개선 요약)도 뺐다 — 마우스를 올리면 나타나는 분류 줄을 가렸다(사용자 요청 2026-10-24) */}
                            </li>
                        ))}
                    </ul>

                    <div className="tcase-detail">
                        {!selected ? (
                            <div className="tcase-empty">{t('assistant:caseList.pick')}</div>
                        ) : (
                            <>
                                {/* 사례 본문 — 리포트와 같은 문서 보기. 실행계획은 칸을 맞추고(planAlign) 한글은 2칸 상자로 그린다.
                                    제목·편집·전체/축약·문서/원문은 고정 줄(header·inline) — 본문만 스크롤된다(2026-10-11). 편집기 삽입 단추는 뺐다(사용자 요청) */}
                                <ReportView
                                    key={`${selected.id}:${versionView ? versionView.version.id : 'now'}`}
                                    markdown={caseDocument(versionView || selected, t, topicCatalog, { brief: caseView === 'brief', meta: false })}
                                    loadingText={t('common:state.loading')}
                                    header={(
                                        <>
                                            <div className="tcase-detailTop">
                                                <h3><CaseTitle title={(versionView || selected).title} /></h3>
                                                {/* 편집은 **본인 것만** 된다. 서버가 남의 id 로는 저장을 거부하므로,
                                                    버튼을 남에게도 보여 주면 눌러 본 뒤에야 안 되는 걸 알게 된다. */}
                                                {selected.mine ? (
                                                    <span className="tcase-detailBtns">
                                                        {/* 「복제 후 편집」 — 원본을 두고 복제본을 같은 등록 화면에서 편집한다(변조는 그 안의 선택 단계). 복제본이면 그 자리에서 편집 */}
                                                        {!selected.copyOf && (
                                                            <button type="button" className="tcase-insert" title={t('assistant:caseCopy.hint')}
                                                                onClick={() => setEditing({ case: { ...selected, id: undefined, copyOf: selected.id, nonce: `copy_${selected.id}_${Date.now()}` } })}>
                                                                <FiCopy size={11} /> {t('assistant:caseCopy.button')}
                                                            </button>
                                                        )}
                                                        <button type="button" className="tcase-insert" onClick={() => setEditing({ case: selected })}>
                                                            <FiEdit3 size={11} /> {t('assistant:caseForm.edit')}
                                                        </button>
                                                    </span>
                                                ) : null}
                                            </div>
                                            {/* 원본 <-> 복제본 링크(주인에게만 온다) */}
                                            {(selected.original || (selected.copies || []).length > 0) && (
                                                <div className="tcase-links">
                                                    {selected.original && (
                                                        <span>
                                                            {t('assistant:caseCopy.original')}:{' '}
                                                            {selected.original.missing
                                                                ? <i>{t('assistant:caseCopy.originalMissing')}</i>
                                                                : <button type="button" className="tcase-link" onClick={() => openCase(selected.original.id)}><CaseTitle title={selected.original.title} /></button>}
                                                        </span>
                                                    )}
                                                    {(selected.copies || []).length > 0 && (
                                                        <span>
                                                            {t('assistant:caseCopy.copies')}:{' '}
                                                            {selected.copies.map(cp => (
                                                                <button key={cp.id} type="button" className="tcase-link" onClick={() => openCase(cp.id)}
                                                                    title={cp.folder || undefined}>
                                                                    <CaseTitle title={cp.title} />
                                                                </button>
                                                            ))}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                            {/* 지난 판을 보는 중 — 지금 판으로 돌아가거나 그 판으로 되돌린다 */}
                                            {versionView && (
                                                <div className="tcase-versionBanner">
                                                    <FiClock size={12} />
                                                    <span>{t('assistant:caseVersion.viewing', { no: versionView.version.versionNo, at: storedTime(versionView.version.savedAt, 'min') })}</span>
                                                    <button type="button" className="tcase-insert" onClick={() => setVersionView(null)}>{t('assistant:caseVersion.backToNow')}</button>
                                                    <button type="button" className="tcase-insert" title={t('assistant:caseVersion.restoreHint')}
                                                        onClick={() => { const { version, mine, ...rest } = versionView; setVersionView(null); setEditing({ case: { ...rest, id: selected.id } }); }}>
                                                        <FiRotateCcw size={11} /> {t('assistant:caseVersion.restore')}
                                                    </button>
                                                </div>
                                            )}
                                            {selected.status === 'draft' && (
                                                <div className="tcase-warn"><FiAlertCircle size={11} /> {t('assistant:caseList.draftWarn')}</div>
                                            )}
                                        </>
                                    )}
                                    inline
                                    lead={<CaseMetaChips c={versionView || selected} t={t} />}
                                    toolbar={(
                                        <span className="stn-row stn-report-mode" role="group" aria-label={t('common:stn.reportScope')}>
                                            <button type="button" className={`app-btn${caseView === 'full' ? ' on' : ''}`} aria-pressed={caseView === 'full'} onClick={() => setCaseView('full')}>{t('common:stn.reportFull')}</button>
                                            <button type="button" className={`app-btn${caseView === 'brief' ? ' on' : ''}`} aria-pressed={caseView === 'brief'} onClick={() => setCaseView('brief')} title={t('assistant:caseDoc.briefHint')}>{t('common:stn.reportBrief')}</button>
                                        </span>
                                    )}
                                    // 과정 이력(AI 개발 가이드라인 원칙 5) — 서버가 저장할 때마다 남긴다. 사람·AI(채팅) 중 누가 언제, 무엇을 바꿨나.
                                    // 지난 판(최대 10개, 2026-10-24)은 주인에게만 — 보기는 위 문서 자리에, 되돌리기는 편집 화면에 채워 연다(저장은 사람이).
                                    // ReportView 의 footer 로 둔다 — 틀 밖에 두면 맨 아래까지 내릴 때 고정 줄(제목·단추)이 밀려 올라갔다(2026-10-24 신고)
                                    footer={(
                                        <CaseVersions
                                            caseId={selected.id} history={selected.history || []} mine={!!selected.mine}
                                            viewingId={versionView ? versionView.version.id : null}
                                            onView={(v) => setVersionView(v)}
                                            onRestore={(v) => { const { version, mine, ...rest } = v; setVersionView(null); setEditing({ case: { ...rest, id: selected.id } }); }}
                                        />
                                    )}
                                />
                            </>
                        )}
                    </div>
                </div>
                </>
                )}
            </div>
        </div>
    );
}

TuningCaseModal.propTypes = {
    open: PropTypes.bool,
    /** 에이전트가 채워 보낸 사례 양식. 오면 등록 화면을 편집 상태로 연다. */
    // eslint-disable-next-line react/forbid-prop-types -- 저장 구조 그대로다. 여기서 나열하면 양식과 두 곳으로 갈린다
    seed: PropTypes.object,
    onSeedUsed: PropTypes.func,
    onClose: PropTypes.func.isRequired,
    /** (saved, seed) — 저장이 끝났다. seed 는 이 등록을 시작한 양식(에이전트·SQL 튜닝 작업) */
    onCaseSaved: PropTypes.func,
    /** 초안을 만들 LLM 경로. 패널의 계정 배지가 가리키는 것과 같아야 한다. */
    provider: PropTypes.string,
    /** 튜닝 가이드 관리(설계·튜닝 가이드 창의 튜닝 탭)를 연다 */
    onOpenGuides: PropTypes.func,
    /** 새로고침 전에 보던 사례 { id } — 처음 열릴 때 한 번 다시 연다 */
    restore: PropTypes.shape({ id: PropTypes.string }),
    /** 고른 사례가 바뀔 때 ({ id }) — App 이 배치에 저장한다 */
    onView: PropTypes.func,
};

// 작업 탭을 오갈 때 App 이 새 콜백을 넘겨도 창을 통째로 다시 그리지 않는다(common/memoWindow, 2026-10-26 — 탭 전환이 멈칫했다)
export default memoWindow(TuningCaseModal);
